import React, { useState, useCallback, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Badge,
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogTrigger,
} from "@fluentui/react-components";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { FluentTooltip } from "../../../../../components/FluentUI/Fluent-Tooltip";
import { GetTaskForConfigurableTelerikGrid } from "../../../../../services/api/task-telerik-api";
import { LoginContext } from "../../../../../services/api/authentication-api";
import { useFluentToast } from "../../../../../components/FluentUI/Fluent-Toast";
import { CustomCell, ModifyConfigurableTaskData } from "./configurable-task-util";
import TaskFormsDetailGrid from "./task-forms-detail-grid";
import {
  submitButton
} from "../../../../new-dashboard/dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import "./task-forms-detail-modal.scss";
import { SkeletonBody } from "../../../../../components/skeleton/skeleton-body";
import { Skeleton } from "@progress/kendo-react-indicators";

const TaskFormsDetailModal = ({
  show,
  onHide,
  taskId,
  taskData: initialTaskData,
  selectedTaskRow,
  GetTasks,
  loading: loadingFromParent,
  isArchived,
  taskStatusId,
}) => {
  const user = useContext(LoginContext);
  const labels = user?.currentUser?.objectLabels;
  const { showToast, FluentToast } = useFluentToast();
  const [allColumns, setAllColumns] = useState(null);
  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveState, setSaveState] = useState({
    hasChanges: false,
    handleSave: null,
    isSaving: false,
  });

  // Update taskData when initialTaskData changes (from parent)
  useEffect(() => {
    if (initialTaskData) {
      // The parent passes either a single task object or an object with data array
      const task = Array.isArray(initialTaskData?.data) 
        ? initialTaskData.data[0] 
        : initialTaskData;
      setAllColumns(initialTaskData?.allColumns || task?.allColumns);
      setTaskData(task);
    } else {
      setAllColumns(null);
      setTaskData(null);
    }
  }, [initialTaskData]);

  // This function is only called when we need to refresh the data (e.g., after saving)
  const fetchTaskData = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      let result = await GetTaskForConfigurableTelerikGrid(taskId);
      let modifiedResult = await ModifyConfigurableTaskData(
        result?.data?.data,
        user?.currentUser?.department?.DateFormatID
      );
      
      // Extract Forms from the response
      const forms = result?.data?.Forms || [];
      
      // Get the processed task (first item from the array) and combine with Forms
      const processedTask = modifiedResult && modifiedResult.length > 0 
        ? modifiedResult[0] 
        : null;
      
      if (processedTask) {
        const processedTaskData = {
          ...processedTask,
          Forms: forms,
        };
        setTaskData(processedTaskData);
        setAllColumns(result?.data?.allColumns);
      } else {
        showToast("Error", "No task data returned from server", "error");
      }
    } catch (error) {
      console.error("Error fetching task:", error);
      showToast("Error", "Error fetching task data", "error");
    } finally {
      setLoading(false);
    }
  }, [taskId, showToast, user]);

  // Disabling the refresh function for now
  const handleRefresh = useCallback(() => {
    // Refresh the task data after saving
    //fetchTaskData();
    // Also refresh the main grid
    if (GetTasks) {
      //GetTasks();
    }
  }, [fetchTaskData, GetTasks]);

  const handleSaveReady = useCallback((state) => {
    setSaveState(state);
  }, []);

  // Handle submit action
  const handleSubmit = useCallback(() => {
    if (saveState.handleSave) {
      saveState.handleSave();
    }
  }, [saveState.handleSave]);

  // Create submit button using helper
  const isSubmitDisabled = !saveState.hasChanges || saveState.isSaving || !saveState.handleSave;
  const submitBtn = submitButton(isSubmitDisabled, handleSubmit);
  
  // Memoize columns to prevent unnecessary re-renders
  const headerColumns = useMemo(() => {
    if (!allColumns || !taskData) return [];
    return allColumns
      .filter((column) => {
        return column !== "Actions" && taskData?.[column]?.Hidden !== true;
      })
      .map((column) => (
        <Column
          key={`header-${column}`}
          field={`${column}.Data`}
          title={taskData?.[column]?.Title || column}
          width={150}
          cells={{
            data: (props) => CustomCell(column, props),
          }}
        />
      ));
  }, [allColumns, taskData]);
  
  // Memoize grid data to prevent re-renders
  const gridDataArray = useMemo(() => {
    return taskData ? [taskData] : [];
  }, [taskData]);

  // Add/remove body class when modal opens/closes to style Bootstrap tooltips
  useEffect(() => {
    if (show) {
      document.body.classList.add('task-forms-modal-open');
    } else {
      document.body.classList.remove('task-forms-modal-open');
    }
    return () => {
      document.body.classList.remove('task-forms-modal-open');
    };
  }, [show]);

  return (
    <>
      <Dialog
        open={show}
        modalType="modal"
        onOpenChange={(event, data) => {
          if (!data.open) {
            onHide();
          }
        }}
      >
        <DialogSurface
          aria-describedby={undefined}
          className="task-forms-detail-modal"
          style={{ maxWidth: "95vw", width: "95vw" }}
        >
          <DialogBody>
            <DialogTitle
              className="d-flex justify-content-between"
              action={
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label="close"
                    icon={<FluentUiIcon name="DismissRegular" />}
                  />
                </DialogTrigger>
              }
            >
              <span>
                <span className="subDepartmentTitle">
                  {(loading || loadingFromParent) ? (
                    <Skeleton
                      shape="text"
                      className="skeleton-text"
                      animation={{ type: "wave" }}
                      data-testid="text-skeleton"
                    />
                  ) : <FluentBadge
                    text={taskData?.Project?.Data ?? ""}
                    appearance="tint"
                    size="extra-large"
                  />}
                 
                </span>
                <p className="subDepartmentDescription" style={{ marginTop: "10px", marginBottom: "8px", fontSize: "22px", fontWeight: "bold" }}>
                  {taskData?.Name?.Data || selectedTaskRow?.Name?.Data || "Task Forms"}
                </p>
                <p className="subDepartmentDescription" style={{ margin: 0, fontSize: "14px", color: "#666" }}>
                  {taskData?.Forms?.Data?.length > 0 ? taskData?.Forms?.Data?.length + " " : ""}{labels?.FormPlural ?? "Sub Tasks"}
                </p>
              </span>
              <div className="d-flex align-items-start">
                <span className="ml-2">
                  {submitBtn}
                </span>
              </div>
            </DialogTitle>
            <DialogContent>
              {(loading || loadingFromParent) ? (
                <div>
                  <div className="mb-4">
                    <SkeletonBody width="100%" rowLength={1} colLength={8} addFirstColumnAsIndex={false} />
                  </div>
                  <SkeletonBody width="100%" rowLength={11} colLength={5} />
                </div>
              ) : taskData ? (
                <>
                  {/* Header: Light version of the task row */}
                  {taskData && allColumns && (
                    <div className="task-forms-header-grid mb-3" style={{ height: "100px " }}>
                      <Grid
                        data={gridDataArray}
                        backgroundColor="#fafafa"
                        style={{ overflowY: "hidden", marginBottom: "20px", height: "100px" }}
                      >
                        {headerColumns}
                      </Grid>
                    </div>
                  )}

                  {/* Detail Grid: Forms */}
                  {taskData?.Forms?.Data && Array.isArray(taskData.Forms?.Data) && taskData.Forms.Data.length > 0 ? (
                    <TaskFormsDetailGrid
                      forms={taskData.Forms.Data}
                      taskId={taskId}
                      onSave={handleRefresh}
                      showToast={showToast}
                      onSaveReady={handleSaveReady}
                      isArchived={isArchived}
                      taskStatusId={taskStatusId}
                    />
                  ) : (
                    <div className="p-3">No forms available for this task.</div>
                  )}
                </>
              )
                : (
                  <div className="p-3">No task data available.</div>
                )
              }
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {FluentToast}
    </>
  );
};

TaskFormsDetailModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  taskId: PropTypes.number.isRequired,
  taskData: PropTypes.object,
  selectedTaskRow: PropTypes.object,
  GetTasks: PropTypes.func,
  allColumns: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  isArchived: PropTypes.bool,
  taskStatusId: PropTypes.number,
};

export default TaskFormsDetailModal;
